// Application-level settings service that doesn't rely on user authentication
import { isSupabaseConfigured, getSupabaseUrl, getSupabaseAnonKey } from '../config/supabase';
import { getSupabaseAuthHeaders } from '../utils/supabaseWrapper';

/**
 * Save global app settings using service key authentication (bypassing user auth)
 * This approach uses the anon key directly for administrative operations
 */
export const saveGlobalAppSetting = async (key, value) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured properly.');
  }

  try {
    // Use centralized Supabase configuration
    const supabaseUrl = getSupabaseUrl();
    const supabaseKey = getSupabaseAnonKey();

    if (!supabaseKey) {
      throw new Error('Supabase anon key not configured. Please set up in admin settings.');
    }

    // First, check if the key already exists
    const checkResponse = await fetch(`${supabaseUrl}/rest/v1/app_settings?key=eq.${key}&select=id`, {
      headers: await getSupabaseAuthHeaders()
    });

    if (checkResponse.ok) {
      const existingRecords = await checkResponse.json();
      
      if (existingRecords.length > 0) {
        // Key exists, use UPDATE
        console.log('🔄 Key exists, updating existing record...');
        return await updateGlobalAppSetting(key, value);
      }
    }

    // Key doesn't exist, use INSERT
    console.log('🆕 Key doesn\'t exist, inserting new record...');
    const response = await fetch(`${supabaseUrl}/rest/v1/app_settings`, {
      method: 'POST',
      headers: await getSupabaseAuthHeaders({ 'Prefer': 'return=representation' }),
      body: JSON.stringify({
        key: key,
        value: value,
        is_active: true,
        updated_at: new Date().toISOString(),
        // Don't include updated_by - let it be null for system settings
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ REST API error:', response.status, errorText);
      
      // If it's still a duplicate key error (race condition), try UPDATE
      if (response.status === 409) {
        console.log('🔄 Race condition detected, trying UPDATE...');
        return await updateGlobalAppSetting(key, value);
      }
      
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    let data = null;
    try {
      const responseText = await response.text();
      if (responseText.trim()) {
        data = JSON.parse(responseText);
      } else {
        data = { success: true };
      }
    } catch (jsonErr) {
      data = { success: true };
    }
    return { data, error: null };

  } catch (err) {
    console.error('❌ Error saving app setting:', err);
    throw err;
  }
};

/**
 * Update existing global app setting using direct REST API
 */
const updateGlobalAppSetting = async (key, value) => {
  try {
    // Use centralized Supabase configuration
    const supabaseUrl = getSupabaseUrl();
    const supabaseKey = getSupabaseAnonKey();

    if (!supabaseKey) {
      throw new Error('Supabase anon key not configured. Please set up in admin settings.');
    }

    console.log(`🔄 Updating app setting: ${key} = ${value}`);

    const response = await fetch(`${supabaseUrl}/rest/v1/app_settings?key=eq.${key}`, {
      method: 'PATCH',
      headers: await getSupabaseAuthHeaders({ 'Prefer': 'return=representation' }),
      body: JSON.stringify({
        value: value,
        is_active: true,
        updated_at: new Date().toISOString(),
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ UPDATE failed for key ${key}:`, response.status, errorText);
      throw new Error(`UPDATE failed - HTTP ${response.status}: ${errorText}`);
    }

    let data = null;
    try {
      const responseText = await response.text();
      if (responseText.trim()) {
        data = JSON.parse(responseText);
      } else {
        data = { success: true };
      }
    } catch (jsonErr) {
      data = { success: true };
    }
    
    console.log(`✅ Successfully updated app setting: ${key} = ${value}`);
    return { data, error: null };

  } catch (err) {
    console.error('❌ Error updating app setting:', err);
    throw err;
  }
};

/**
 * Get global app settings using direct REST API
 */
export const getGlobalAppSetting = async (key) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured');
  }

  try {
    // Use centralized Supabase configuration
    const supabaseUrl = getSupabaseUrl();
    const supabaseKey = getSupabaseAnonKey();

    if (!supabaseKey) {
      throw new Error('Supabase anon key not configured. Please set up in admin settings.');
    }


    const response = await fetch(`${supabaseUrl}/rest/v1/app_settings?key=eq.${key}&is_active=eq.true&select=value`, {
      headers: await getSupabaseAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    let data = null;
    try {
      const responseText = await response.text();
      if (responseText.trim()) {
        data = JSON.parse(responseText);
      } else {
        data = [];
      }
    } catch (jsonErr) {
      console.error('JSON parse error on GET:', jsonErr);
      data = [];
    }
    
    const value = data.length > 0 ? data[0].value : null;
    return { data: value, error: null };

  } catch (err) {
    console.error('❌ Error getting app setting:', err);
    return { data: null, error: err };
  }
};


/**
 * Save TMDB API key using app-level authentication
 */
export const saveTMDBApiKeyAppLevel = async (apiKey) => {
  return await saveGlobalAppSetting('tmdb_api_key', apiKey);
};

/**
 * Get TMDB API key using app-level authentication
 */
export const getTMDBApiKeyAppLevel = async () => {
  return await getGlobalAppSetting('tmdb_api_key');
};

/**
 * Save TMDB Bearer token using app-level authentication
 */
export const saveTMDBBearerTokenAppLevel = async (bearerToken) => {
  return await saveGlobalAppSetting('tmdb_bearer_token', bearerToken);
};

/**
 * Get TMDB Bearer token using app-level authentication
 */
export const getTMDBBearerTokenAppLevel = async () => {
  return await getGlobalAppSetting('tmdb_bearer_token');
};

/**
 * Save admin default tab setting
 */
export const saveAdminDefaultTabAppLevel = async (defaultTab) => {
  return await saveGlobalAppSetting('admin_default_tab', defaultTab);
};

/**
 * Get admin default tab setting
 */
export const getAdminDefaultTabAppLevel = async () => {
  return await getGlobalAppSetting('admin_default_tab');
};

/**
 * Save admin debug mode setting
 */
export const saveAdminDebugModeAppLevel = async (debugMode) => {
  return await saveGlobalAppSetting('admin_debug_mode', debugMode.toString());
};

/**
 * Get admin debug mode setting
 */
export const getAdminDebugModeAppLevel = async () => {
  const result = await getGlobalAppSetting('admin_debug_mode');
  return result === 'true';
};

/**
 * Save global theme setting (admin-controlled, affects all users)
 */
export const saveGlobalThemeAppLevel = async (themeId) => {
  return await saveGlobalAppSetting('global_theme', themeId);
};

/**
 * Get global theme setting
 */
export const getGlobalThemeAppLevel = async () => {
  return await getGlobalAppSetting('global_theme');
};
